import { useEffect, useState, useMemo } from "react";
import { fetchDashboardStats, fmtMoney } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/Common";
import {
  Users,
  Car,
  ClipboardText,
  Package,
  TrendUp,
  ArrowDown,
  ArrowUp,
  Wallet,
} from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const MONTH_NAMES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const CHART_CURSOR = { fill: "rgba(0, 176, 240, 0.06)" };
const CHART_TOOLTIP_STYLE = { background: "#FFF", border: "1px solid #E2E8F0", borderRadius: 6 };
const CHART_BAR_RADIUS = [6, 6, 0, 0];

const monthLabel = (k) => {
  const [, m] = k.split("-");
  return MONTH_NAMES[parseInt(m, 10) - 1];
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchDashboardStats().then(setStats).catch(() => setStats(null));
  }, []);

  const chartData = useMemo(
    () => (stats?.faturamento_mensal || []).map((d) => ({ ...d, mes: monthLabel(d.mes) })),
    [stats]
  );

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Visão geral da oficina"
        subtitle="Indicadores de operação, financeiro e estoque em tempo real."
        testid="dashboard-header"
      />

      {/* Top stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testid="stat-clients" label="Clientes" value={stats?.total_clients ?? "—"} icon={Users} />
        <StatCard testid="stat-vehicles" label="Veículos" value={stats?.total_vehicles ?? "—"} icon={Car} />
        <StatCard testid="stat-open-os" label="OS em aberto" value={stats?.open_os ?? "—"} icon={ClipboardText} accent />
        <StatCard testid="stat-low-stock" label="Estoque baixo" value={stats?.low_stock_count ?? "—"} icon={Package} />
      </div>

      {/* Financial cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          testid="stat-receivable"
          label="A receber (pendente)"
          value={stats ? fmtMoney(stats.a_receber_pendente) : "—"}
          icon={ArrowUp}
        />
        <StatCard
          testid="stat-payable"
          label="A pagar (pendente)"
          value={stats ? fmtMoney(stats.a_pagar_pendente) : "—"}
          icon={ArrowDown}
        />
        <StatCard
          testid="stat-received"
          label="Recebido"
          value={stats ? fmtMoney(stats.recebido_total) : "—"}
          icon={Wallet}
        />
        <StatCard
          testid="stat-balance"
          label="Saldo"
          value={stats ? fmtMoney(stats.saldo) : "—"}
          icon={TrendUp}
          accent
        />
      </div>

      {/* Chart + low stock */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-md p-6" data-testid="revenue-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#475569] mb-1">Faturamento</div>
              <h3 className="font-heading text-xl font-semibold text-[#0F172A]">Últimos 6 meses</h3>
            </div>
          </div>
          <div className="h-[280px]">
            {stats && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="mes" stroke="#475569" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#475569" fontSize={12} tickLine={false} axisLine={false}
                    tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    cursor={CHART_CURSOR}
                    contentStyle={CHART_TOOLTIP_STYLE}
                    formatter={(v) => fmtMoney(v)}
                  />
                  <Bar dataKey="valor" fill="#001E50" radius={CHART_BAR_RADIUS} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-md p-6" data-testid="low-stock-card">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#475569] mb-1">Atenção</div>
          <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-4">Peças em estoque baixo</h3>
          {!stats?.low_stock_items?.length && (
            <p className="text-sm text-[#475569]">Nenhuma peça com estoque abaixo do mínimo.</p>
          )}
          <ul className="space-y-2">
            {stats?.low_stock_items?.map((p) => (
              <li key={p.id} className="flex items-center justify-between text-sm py-2 border-b border-[#E2E8F0] last:border-0">
                <div>
                  <div className="font-medium text-[#0F172A]">{p.nome}</div>
                  <div className="text-xs text-[#475569]">{p.codigo || "Sem código"}</div>
                </div>
                <span className="chip chip-low">{p.quantidade_estoque} un</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
